import MyAxios from '@/api/axios'

import {type AxiosError, type AxiosResponse} from 'axios'
import {IProduct, IProductPrivate, Product} from "@/interface/services/response/product.interface"
import {ICreateOrderDTO, INewOrder, IProductOptionsDTO, IProductPromotionDTO} from "@/interface"
import {CreateOrders, OptionsByProduct, ProductPayload, PromotionByProductCustomer} from "@/models"
import {CommonStore} from "@/stores/common"
import {useAbortController} from '@/composables/useAbortController'
import {getCommonStore, getContractStore, getCustomerStore, getNewOrderStore} from '@/helpers/storeHelpers'

export default
{
    async getContractProducts(arrayIds: number[], id: number, contractId: number): Promise<IProductPrivate[] | void> {
        const { getSignal } = useAbortController();
        const url = `/customer/${id}/contract/${contractId}/products`;
        return await MyAxios.get(url, { params: { ids: arrayIds }, ignoreGlobalLoader: true, signal: getSignal() }).then(
            (response: AxiosResponse<IProductPrivate[]>) => response.data
        );
    },

    async getPodium(): Promise<Product[] | void> {
        const url = `/products/podium`;
        return await MyAxios.get(url).then(
            (response: AxiosResponse<Product[]>) => response.data?.map((el: IProduct) => new Product(el))
        );
    },

    async getOptionsByProduct(commonStore: CommonStore|null, productId: number): Promise<OptionsByProduct | null> {
        commonStore?.startLoading(true, true);
        
        const url = `/products/${productId}/options`;
        return await MyAxios.get(url, { ignoreGlobalLoader: true, ignoreGlobalError: true }).then(
            (response: AxiosResponse<IProductOptionsDTO>) => response.data ? OptionsByProduct.fromDto(response.data) : null
        ).catch((error) => {
            console.error(error)
            return null;
        }).finally(() => {
            commonStore?.stopLoading()
        });
    },

    async getPromotionsByProduct(productId: number): Promise<PromotionByProductCustomer | null> {
        const url = `/products/${productId}/promotions`;
        return await MyAxios.get(url).then(
            (response: AxiosResponse<IProductPromotionDTO>) => PromotionByProductCustomer.fromDto(response.data)
        );
    },

    async getAllPromotionsByProduct(productsIds: number[]|null):Promise<Awaited<PromotionByProductCustomer | null>[] | null> {
        if (!productsIds || productsIds.length <= 0){
            return null;
        }

        const promiseArray = productsIds.map(id => this.getPromotionsByProduct(id));
        return Promise.all(promiseArray).then(promo => {
            return promo.filter(element => element?.promotions && element?.promotions?.length > 0);
        });
    },

    async updateSubscription(): Promise<CreateOrders | void> {
        const commonStore = getCommonStore()
        const customerStore = getCustomerStore()
        const contractStore = getContractStore()
        const newOrderStore = getNewOrderStore()
        const payload = ProductPayload.toDto(newOrderStore?.newOrder ?? {} as INewOrder)

        commonStore?.startLoading(true, true);

        const url = newOrderStore?.isPendingProduct && contractStore?.currentProductBaseId !== newOrderStore.newOrder.id ?
            `/customer/${customerStore?.id}/contract/${contractStore?.currentId}/orders/${newOrderStore.orderPendingId}/modify` :
            `/customer/${customerStore?.id}/contract/${contractStore?.currentId}/orders/create`;

        return await MyAxios.post(url, payload, { ignoreGlobalLoader: true })
            .then((response:AxiosResponse<ICreateOrderDTO[]>) => {
                commonStore?.stopLoading();
                return CreateOrders.fromDto(response.data);
            })
            .catch((error:AxiosError) => {
                commonStore?.stopLoading();
                console.error(error);
            })
    },

    async cancelSubscriptionPending(orderId: string, payload: {internalId: number} | null = null): Promise<ICreateOrderDTO[]|void> {
        const commonStore = getCommonStore()
        const customerStore = getCustomerStore()
        const contractStore = getContractStore()

        commonStore?.startLoading(true, true);

        const url = `/customer/${customerStore?.id}/contract/${contractStore?.currentId}/orders/${orderId}/cancel`;
        return await MyAxios.post(url, payload, { ignoreGlobalLoader: true })
            .then((response: AxiosResponse<ICreateOrderDTO[]>) => {
                return response.data;
            })
            .catch((error: AxiosError) => {
                console.error(error);
            })
            .finally(() => {
                commonStore?.stopLoading()
            })
    }
}
